/**
 * ChatRail — left-side composer panel with a mode switcher.
 *
 * Modes:
 *   - planning: the Planning Agent conversation (PlanningRailPanel)
 *   - chat: the real composer AI Chat panel (ChatPanel)
 *
 * Pushing a Prompt from the Vault flips the rail to "chat" via the
 * `rail:setMode` composerBus event; ChatPanel itself owns the `chat:insert`
 * subscription that populates its textarea.
 */
import { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { composerBus, type RailMode } from "@/lib/composerBus";
import { usePlanningContext } from "../planningContextCore";
import { PlanningRailPanel } from "../PlanningRailPanel";
import { ChatPanel } from "../ChatPanel";

export const ChatRail = () => {
  const planning = usePlanningContext();
  const [mode, setMode] = useState<RailMode>("chat");

  // External mode-switch events (e.g. Vault prompt-push → flip to chat,
  // planning intake → flip to planning). Push routing is the source of truth.
  useEffect(() => {
    return composerBus.subscribe("rail:setMode", (payload) => {
      setMode(payload.mode);
    });
  }, []);

  const workspaceId = planning?.workspaceId ?? null;
  const pieceId = planning?.brief?.id ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <Select value={mode} onValueChange={(v) => setMode(v as RailMode)}>
          <SelectTrigger className="h-7 w-[170px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="planning">Planning Agent</SelectItem>
            <SelectItem value="chat">AI Chat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="min-h-0 flex-1">
        {mode === "planning" ? (
          <PlanningRailPanel />
        ) : workspaceId && pieceId ? (
          <div className="h-full p-2">
            <ChatPanel workspaceId={workspaceId} pieceId={pieceId} />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Open a Piece to start chatting with Excerptus AI.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
